import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { DownloadDatapointRequest } from "../../model/download/download-datapoint-request";
import { DownloadDatapointDetailsRequest } from "../../model/download/download-datapoint-details-request";
import { Point } from "../../model/geometry/point";
import { PoiClosestFilter } from "src/app/model/datapoint/filter/poi-closest-filter";
import { DatapointsClimateChartService } from "src/app/dataset/datapoints/climate-charts/datapoints-climate-chart.service";
import { ActivatedRoute } from "@angular/router";
import { ClimateChart } from "../../model/climate/climate-chart";
import { ClimateChartHelper } from "src/app/dataset/datapoints/climate-charts/util/climate-chart-helper";


@Injectable({
    providedIn: "root",
})
export class DownloadService {
    constructor(
        private readonly http: HttpClient,
        private datapointsClimateChartService: DatapointsClimateChartService,
        private readonly route: ActivatedRoute,
        
    ) {}

    downloadDrafts(
        datasetID: string,
        request: any,
        fileName: string
    ): Observable<any> {
        return this.http.post(`/drafts/dataset/${datasetID}/file`, request, {
            params: { fileName: fileName },
            responseType: "blob",
        });
    }

    // POST /v2/datapoints/dataset/{datasetID}/file
    downloadDatapoints(
        datasetID: string,
        request: DownloadDatapointRequest,
        fileName: string
    ): Observable<any> {
        let downloadFileName = 'cluster-content.xlsx';
        if (fileName === downloadFileName) {
            return this.http.post(
                `/clustering/datapoints/download/file`,
                request,
                {
                    params: { fileName: fileName },
                    responseType: "blob",
                }
            );
        } else {
            return this.http.post(
                `/datapoints/dataset/${datasetID}/file`,
                request,
                {
                    params: { fileName: fileName },
                    responseType: "blob",
                }
            );
        }
    }

    downloadDatapointsByIds(
        datasetID: string,
        request: DownloadDatapointRequest,
        fileName: string
    ): Observable<any> {
        return this.http.post(
            `/datapoints/dataset/${datasetID}/file/by-ids`,
            request,
            {
                params: { fileName: fileName },
                responseType: "blob",
            }
        );
    }

    downloadDatasetFields(
        datasetID: string,
        fileName: string,
        filter?: string
    ): Observable<any> {
        const body = filter ? { filter } : null;
        return this.http.post(`/fields/dataset/${datasetID}/file`, body, {
            params: { fileName },
            responseType: "blob",
        });
    }

    /**
     * Cleans a single value by replacing null, undefined, NaN, or empty values with "-"
     * @param value - The value to clean
     * @param index - The index in the array (0 = series name, kept as-is)
     * @returns Cleaned value
     */
    private cleanChartValue(value: string | number | null | undefined, index: number): string | number {
        if (index === 0) return value as string; // Keep the series name as-is
        if (value === null || value === undefined || value === '' || value === '-') return '-';
        if (typeof value === 'number' && isNaN(value)) return '-';
        if (typeof value === 'string' && value.toLowerCase() === 'nan') return '-';
        return value;
    }

    /**
     * Transforms a raw ClimateChart object into a formatted table structure for download.
     * Applies the same padding logic used by the UI (padSSPScenarioData) so that:
     * - Columns align correctly with year range headers
     * - Missing values are represented as "-" instead of NaN
     * - Historical row gets trailing dashes for future periods
     * - SSP rows get a leading dash for the historical period
     *
     * Returns: Formatted chart data (series as object for JSON serialization)
     */
    private formatClimateChartForDownload(chart: ClimateChart): Record<string, unknown> | null {
        if (!chart || !chart.series || !chart.xAxis) {
            return chart as unknown as Record<string, unknown>;
        }

        const formattedSeries: { [key: string]: (string | number)[] } = {};
        const xAxis = chart.xAxis;

        // Iterate over series entries and apply padding
        const seriesObj = chart.series;
        const entries = seriesObj instanceof Map
            ? Array.from(seriesObj.entries())
            : Object.entries(seriesObj);

        for (const [key, value] of entries) {
            if (!Array.isArray(value)) continue;
            // Apply the same padding logic used by the UI charts
            const padded = ClimateChartHelper.padSSPScenarioData(key, value, xAxis);
            // Replace any null, undefined, NaN, or empty values with "-"
            const cleaned = padded.map((v, index) => this.cleanChartValue(v, index));
            formattedSeries[key] = cleaned;
        }

        return {
            ...chart,
            series: formattedSeries
        } as unknown as Record<string, unknown>;
    }

    /**
     * Transforms a climate data payload, applying formatting to each non-null chart.
     * @param payload - Object containing ClimateChart entries
     * @returns Formatted payload with cleaned chart data
     */
    private formatClimatePayload(payload: Record<string, ClimateChart | null>): Record<string, unknown> {
        if (!payload) return payload;
        const formatted: Record<string, unknown> = {};
        for (const [key, chart] of Object.entries(payload)) {
            formatted[key] = chart ? this.formatClimateChartForDownload(chart) : null;
        }
        return formatted;
    }

    /**
     * Transforms the entire climatology payload, applying formatting to each non-null chart.
     * @param payload - Object containing ClimateChart entries
     * @returns Formatted payload with cleaned chart data
     */
    private formatClimatologyPayload(payload: Record<string, ClimateChart | null>): Record<string, unknown> {
        return this.formatClimatePayload(payload);
    }

    /**
     * Appends the Region (bioclimatic zone) charts in the Region tab's order. The download writes
     * them together, after the consolidated risk score blocks.
     */
    private withRegionCharts(payload: Record<string, ClimateChart | null>): Record<string, ClimateChart | null> {
        const regionCharts: Record<string, ClimateChart | null> = {};
        Object.entries(this.datapointsClimateChartService.regionDownloadPayload)
            .forEach(([code, chart]) => regionCharts[`${code}Region`] = chart);
        return { ...payload, ...regionCharts };
    }

    /**
     * Adds the instant calculation result to the climatology payload when one has been run, so the
     * Climate Change sheet carries it as its own labelled block beside the stored one (FR-1.66).
     * Added here rather than held in the payload object itself: that object is keyed by indicator
     * and is reset per location, while this exists only for the life of one download.
     */
    private withInstantConsolidatedRiskScore(
        payload: Record<string, ClimateChart | null>
    ): Record<string, ClimateChart | null> {
        // Carried whenever a calculation has been performed, whichever view the download is taken
        // from: the workbook is the same document either way.
        const instant = this.datapointsClimateChartService.consolidatedRiskScoreInstantChart;
        return instant ? { ...payload, consolidatedRiskScoreInstant: instant } : payload;
    }

    /**
     * Sends all data in request body to avoid 414 URI Too Long errors
     * URL path includes coordinates: /external/{x}/{y}/download
     * Backend must accept @RequestBody with all parameters
     */
    downloadExternalDataForAddress(
        point: Point,
        accountDatasetId: string,
        address: string,
        datasetIds: any,
        poiClosestFilter: PoiClosestFilter
    ): Observable<any> {
        // Send ALL parameters in request body (no query params)
        const requestBody = {
            datasetIds: datasetIds,
            accountDatasetId: accountDatasetId,
            address: address,
            longitude: poiClosestFilter.longitude,
            latitude: poiClosestFilter.latitude,
            externalDatasetId: poiClosestFilter.externalDatasetId,
            distanceInMeters: poiClosestFilter.distanceInMeters,
            distanceUnit: poiClosestFilter.distanceUnit,
            limit: poiClosestFilter.limit,
            climateData: this.formatClimatePayload(this.datapointsClimateChartService.downloadRequestPayload),
            climatologyData: this.formatClimatologyPayload(this.withInstantConsolidatedRiskScore(
                this.withRegionCharts(this.datapointsClimateChartService.downloadClimatologyRequestPayload))),
            // Already loaded for the profile fields, so the External sheet does not look it up again.
            bioclimaticRegion: this.datapointsClimateChartService.bioclimaticRegion
        };

        // Keep coordinates in URL path
        const url = `/external/${point.x}/${point.y}/download`;
        return this.http.post(url, requestBody, {
            responseType: "blob"
        });
    }

    /**
     * V2: Sends all data in request body to avoid 414 URI Too Long errors
     * Backend must accept @RequestBody with all parameters
     */
    downloadLocationProfile(
        datapointId: string,
        datasetId: string,
        poiClosestFilter: PoiClosestFilter
    ): Observable<any> {
        // Send ALL parameters in request body (no query params)
        const requestBody = {
            datapointId: datapointId,
            datasetId: datasetId,
            longitude: poiClosestFilter.longitude,
            latitude: poiClosestFilter.latitude,
            externalDatasetId: poiClosestFilter.externalDatasetId,
            distanceInMeters: poiClosestFilter.distanceInMeters,
            distanceUnit: poiClosestFilter.distanceUnit,
            limit: poiClosestFilter.limit,
            climateData: this.formatClimatePayload(this.datapointsClimateChartService.downloadRequestPayload),
            climatologyData: this.formatClimatologyPayload(this.withInstantConsolidatedRiskScore(
                this.withRegionCharts(this.datapointsClimateChartService.downloadClimatologyRequestPayload))),
            // Already loaded for the profile fields, so the Summary sheet does not look it up again.
            bioclimaticRegion: this.datapointsClimateChartService.bioclimaticRegion
        };

        return this.http.post("/download/location-profile", requestBody, {
            responseType: "blob",
            observe: "response"
        });
    }

    downloadDashboardPDFFile(
        datasetID: string,
        request: DownloadDatapointRequest,
        fileName: string
    ): Observable<any> {
        return this.http.post(
            `/datapoints/dataset/${datasetID}/file`,
            request,
            {
                params: { fileName: fileName },
                responseType: "blob",
            }
        );
    }

    downloadMunichReReport(
        datasetID: string,
        datapointID: string
    ): Observable<any> {
        return this.http.post(
            `/munichre/download-report/${datasetID}/${datapointID}`,{}
        );
    }

    downloadMunichReReportForSeeInfo(
        latitude: number,
        longitude : number,
        datasetID: string,
        address: string
    ): Observable<any> {
        return this.http.post(
            `/munichre/download-report/${latitude}/${longitude}/${datasetID}/${address}`,{});
    }

    downloadRenewalReport(
        reportId: string,
    ): Observable<any> {
        return this.http.post(
            `/reports/download/${reportId}`,
            {},
            {
                responseType: "blob",
            }
        );
    }

    downloadMunichReReportReportingEdition(
        datasetID: string,
        datapointID: string
    ): Observable<any> {
        return this.http.post(
            `/munichre/report/download-report/${datasetID}/${datapointID}`, {}
        );
    }

    downloadMunichReReportForSeeInfoReportingEdition(
        latitude: number,
        longitude: number,
        datasetID: string,
        address: string
    ): Observable<any> {
        return this.http.post(
            `/munichre/report/download-report/${latitude}/${longitude}/${datasetID}/${address}`, {});
    }

    // Download cluster summary as XLSX
    downloadClustersSummary(
        datasetId: string,
        request: any,
        fileName: string
    ): Observable<any> {
        return this.http.post(
            `/clustering/dataset/${datasetId}/download-summary`,
            request,
            {
                params: { fileName: fileName },
                responseType: "blob",
            }
        );
    }
}
